import { useEffect, useRef } from "react";
import { Camera, Video, Palette } from "lucide-react";
import ScrollReveal from "./ScrollReveal";
import { useLanguage } from "@/i18n/LanguageContext";
// Tła sekcji "Usługi" — desktop + mobile. Wyświetlane z opacity 60% + overlay.
import servicesBg from "@/assets/services-bg.jpg";
import servicesBgMobile from "@/assets/services-bg-mobile.jpg";

// Custom drone icon (lucide-compatible)
const DroneIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M10 10 7 7" />
    <path d="m10 14-3 3" />
    <path d="m14 10 3-3" />
    <path d="m14 14 3 3" />
    <circle cx="12" cy="12" r="2" />
    <path d="M4.93 4.93a8 8 0 0 0-.53 10.1" />
    <path d="M19.07 4.93a8 8 0 0 1 .53 10.1" />
    <path d="M4.93 19.07a8 8 0 0 1-.53-10.1" />
    <path d="M19.07 19.07a8 8 0 0 0 .53-10.1" />
  </svg>
);

const ServicesSection = () => {
  const { t } = useLanguage();
  const imgRef = useRef<HTMLImageElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!imgRef.current || !sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const y = -rect.top * 0.2;
      imgRef.current.style.transform = `translateY(${y}px)`;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const services = [
    {
      icon: Video,
      title: t.services.videography.title,
      description: t.services.videography.description,
    },
    {
      icon: Camera,
      title: t.services.photography.title,
      description: t.services.photography.description,
    },
    {
      icon: DroneIcon,
      title: t.services.creativeDirection.title,
      description: t.services.creativeDirection.description,
    },
    {
      icon: Palette,
      title: t.services.branding.title,
      description: t.services.branding.description,
    },
  ];

  return (
    <section ref={sectionRef} id="services" className="relative py-10 md:py-16 px-6 overflow-hidden">
      {/* Background photo with parallax */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden flex items-center justify-center">
        {/* Mobile portrait */}
        <img
          ref={imgRef}
          src={servicesBgMobile}
          alt=""
          loading="lazy"
          decoding="async"
          className="md:hidden absolute inset-0 w-full h-full object-cover opacity-60 will-change-transform"
        />
        {/* Desktop landscape */}
        <img
          src={servicesBg}
          alt=""
          loading="lazy"
          decoding="async"
          className="hidden md:block w-full h-auto object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-background/40" />
      </div>
      <div className="max-w-6xl mx-auto relative z-10">
        <ScrollReveal>
          <span className="block text-center text-accent text-xs font-mono tracking-widest uppercase">
            [ {t.services.label} // 03 ]
          </span>
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <h2 className="mt-4 text-center text-4xl md:text-4xl lg:text-5xl font-bold text-foreground uppercase glitch cursor-default">
            {t.services.heading}
          </h2>
        </ScrollReveal>

        {/* Mobile: compact horizontal-row list */}
        <div className="mt-10 md:hidden space-y-3">
          {services.map((service, index) => (
            <ScrollReveal key={`m-${service.title}`} delay={150 + index * 80}>
              <div className="group relative flex items-center gap-4 p-4 bg-card/90 backdrop-blur-sm border border-border hover:border-accent transition-colors">
                <div className="self-center shrink-0 w-12 h-12 border border-border flex items-center justify-center text-muted-foreground group-hover:text-accent group-hover:border-accent transition-colors">
                  <service.icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm md:text-base font-bold text-foreground uppercase tracking-tight leading-tight">
                      {service.title}
                    </h3>
                    <span className="font-mono text-[10px] text-accent tracking-wider shrink-0 mt-0.5">
                      //0{index + 1}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground font-mono leading-snug">
                    {service.description}
                  </p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Desktop: original 2-col grid */}
        <div className="mt-16 hidden md:grid grid-cols-2 gap-8">
          {services.map((service, index) => (
            <ScrollReveal key={service.title} delay={200 + index * 100} className="h-full">
              <div className="group relative h-full flex flex-col p-8 bg-card/90 backdrop-blur-sm border-2 border-border hover:border-[hsl(var(--accent-hover)/0.9)] transition-all duration-200 hover:translate-x-[-4px] hover:translate-y-[-4px] hover:shadow-[8px_8px_0_0_hsl(var(--accent-hover)/0.9)]">
                <div className="absolute top-2 right-3 font-mono text-[10px] text-accent group-hover:text-[hsl(var(--accent-hover)/0.9)] transition-colors tracking-wider">
                  //0{index + 1}
                </div>
                <service.icon className="w-10 h-10 text-muted-foreground group-hover:text-[hsl(var(--accent-hover)/0.9)] transition-colors" />
                <h3 className="mt-6 text-xl font-bold text-foreground uppercase tracking-tight">
                  {service.title}
                </h3>
                <p className="mt-3 text-muted-foreground leading-relaxed font-mono text-sm">
                  {service.description}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
